import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Plus, X, Save, Edit, Loader2, Trash2, Clock, Layers } from "lucide-react";

import BenchmarksEditor from "../pages/jobDescription/BenchmarksEditor";
import AIQuestionGeneratorModal from "./AIQuestionGeneratorModal";
import QuestionBankPickerModal from "./QuestionBankPickerModal";
import InterviewProfilePickerModal from "../pages/interviewLibrary/InterviewProfilePickerModal";
import api from "../../../api/client";
import { convertMarkdownToHtml } from "../../../utils/markdownToHTML";
import { cleanEmptyListItems } from "../../../utils/htmlSanitizer";
import { useTranslation } from 'react-i18next';
import { calculateInterviewDurationRequirements, formatDurationBreakdownText } from "../../../utils/durationCalculator";

export default function AddJobDescriptionDialog({
  open,
  onClose,
  onSubmit,
  jobDescriptionForm,
  handleInputChange,
  departments,
  selectedJobDescription,
  jobDescriptionError,
  setShowAddDepartmentModal,
}) {
  const [requiredSkillsInput, setRequiredSkillsInput] = useState("");
  const [niceToHaveSkillsInput, setNiceToHaveSkillsInput] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [questionInput, setQuestionInput] = useState("");
  const [questionType, setQuestionType] = useState("concept");
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState("");
  const [editedQuestionType, setEditedQuestionType] = useState("concept");
  const [questionTimeLimit, setQuestionTimeLimit] = useState(5);
  const [editedQuestionTimeLimit, setEditedQuestionTimeLimit] = useState(5);
  const [editedMcqOptions, setEditedMcqOptions] = useState(["", "", "", ""]);
  const [editedMcqCorrectIndex, setEditedMcqCorrectIndex] = useState(0);
  // Pooja Added
  const [, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // const [jobDescription, setJobDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [mcqOptions, setMcqOptions] = useState(["", "", "", ""]);
  const [mcqCorrectIndex, setMcqCorrectIndex] = useState(0);

  const durationInfo = useMemo(() => {
    return calculateInterviewDurationRequirements(jobDescriptionForm.questions || []);
  }, [jobDescriptionForm.questions]);
  const [showAiGeneratorModal, setShowAiGeneratorModal] = useState(false);
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false);
  const [showProfilePickerModal, setShowProfilePickerModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [attachedProfile, setAttachedProfile] = useState(null);

  useEffect(() => {
    if (selectedJobDescription?.attached_profiles && Array.isArray(selectedJobDescription.attached_profiles) && selectedJobDescription.attached_profiles.length > 0) {
      setAttachedProfile(selectedJobDescription.attached_profiles[0]);
    } else {
      setAttachedProfile(null);
    }
  }, [selectedJobDescription, open]);

  const handleProfileAttached = (questions, mode, durationInfo, profileObj) => {
    handleInputChange({
      target: {
        name: "questions",
        value: questions,
      },
    });
    if (profileObj) {
      setAttachedProfile(profileObj);
      handleInputChange({
        target: {
          name: "attached_profile_id",
          value: profileObj.id,
        },
      });
    }
  };

  const handleDetachProfile = async (profileToDetach) => {
    const profId = profileToDetach?.id || attachedProfile?.id;
    const jdId = selectedJobDescription?.id || jobDescriptionForm?.id;
    let serverUpdatedQuestions = null;

    if (jdId && profId) {
      try {
        const res = await api.post(`job-descriptions/${jdId}/detach-profile/`, {
          profile_id: profId,
          current_questions: jobDescriptionForm.questions || [],
        });
        if (res.data?.questions) {
          serverUpdatedQuestions = res.data.questions;
        }
      } catch (err) {
        console.error("Failed to detach profile on server:", err);
      }
    }

    const currentQuestions = jobDescriptionForm.questions || [];
    const remainingQuestions = serverUpdatedQuestions || currentQuestions.filter((q) => {
      const qProfId = q.profileId || q.profile_id;
      if (profId && qProfId) {
        return String(qProfId) !== String(profId);
      }
      if (!profId && qProfId) {
        return false;
      }
      return true;
    });

    setAttachedProfile(null);
    handleInputChange({
      target: {
        name: "questions",
        value: remainingQuestions,
      },
    });
    handleInputChange({
      target: {
        name: "attached_profile_id",
        value: null,
      },
    });
  };

  const handleAiQuestionsAdded = (newQuestions) => {
    const currentQuestions = jobDescriptionForm.questions || [];
    handleInputChange({
      target: {
        name: "questions",
        value: [...currentQuestions, ...newQuestions],
      },
    });
  };
  const { t, i18n } = useTranslation();

  // FIX: Set default work_type when dialog opens if not already set
  useEffect(() => {
    if (open && !jobDescriptionForm.work_type) {
      handleInputChange({
        target: {
          name: "work_type",
          value: "on_site"
        }
      });
    }
  }, [open, jobDescriptionForm.work_type, handleInputChange]);

  const employmentTypes = [
    { value: "full_time", label: t('jobDescription.form.employmentTypes.full_time', 'Full Time') },
    { value: "part_time", label: t('jobDescription.form.employmentTypes.part_time', 'Part Time') },
    { value: "contract", label: t('jobDescription.form.employmentTypes.contract', 'Contract') },
    { value: "freelance", label: t('jobDescription.form.employmentTypes.freelance', 'Freelance') },
    { value: "internship", label: t('jobDescription.form.employmentTypes.internship', 'Internship') },
    { value: "temporary", label: t('jobDescription.form.employmentTypes.temporary', 'Temporary') },
  ];

  const experienceLevels = [
    { value: "entry", label: t('jobDescription.form.experienceLevels.entry', 'Entry Level (0-1 years)') },
    { value: "junior", label: t('jobDescription.form.experienceLevels.junior', 'Junior (1-3 years)') },
    { value: "mid", label: t('jobDescription.form.experienceLevels.mid', 'Mid Level (3-5 years)') },
    { value: "senior", label: t('jobDescription.form.experienceLevels.senior', 'Senior (5-8 years)') },
    { value: "lead", label: t('jobDescription.form.experienceLevels.lead', 'Lead (8+ years)') },
    { value: "executive", label: t('jobDescription.form.experienceLevels.executive', 'Executive') },
  ];

  const interviewStyles = [
    { value: "default", label: t('jobDescription.form.interviewStyles.default', 'Default Interview Style') },
    { value: "campus_recruitment", label: t('jobDescription.form.interviewStyles.campus_recruitment', 'Campus Recruitment') },
    { value: "startup", label: t('jobDescription.form.interviewStyles.startup', 'Startup Style Interview') },
    { value: "enterprise", label: t('jobDescription.form.interviewStyles.enterprise', 'Enterprise Level Interview') },
  ];

  const currentLanguageCode = (i18n.language || "en").split("-")[0];

  const statusOptions = [
    { value: "open", label: t('jobDescription.form.statusOptions.open', 'Open') },
    { value: "closed", label: t('jobDescription.form.statusOptions.closed', 'Closed') },
    { value: "cancelled", label: t('jobDescription.form.statusOptions.cancelled', 'Cancelled') },
    { value: "expired", label: t('jobDescription.form.statusOptions.expired', 'Expired') },
    { value: "archived", label: t('jobDescription.form.statusOptions.archived', 'Archived') },
  ];

  const questionTypeOptions = [
    { value: "concept", label: t('jobDescription.conceptual', 'Conceptual') },
    { value: "coding", label: t('jobDescription.coding', 'Coding') },
    { value: "mcq", label: t('jobDescription.multipleChoice', 'Multiple Choice') },
    { value: "behavioral", label: t('jobDescription.behavioral', 'Behavioral') },
    { value: "general", label: t('jobDescription.general', 'General') },
  ];

  const steps = [
    {
      label: t('jobDescription.steps.basicInformation'),
      description: t('jobDescription.steps.basicInformationDescription'),
    },
    {
      label: t('jobDescription.steps.skillsRequirements'),
      description: t('jobDescription.steps.skillsRequirementsDescription'),
    },
    {
      label: t('jobDescription.steps.descriptionSettings'),
      description: t('jobDescription.steps.descriptionSettingsDescription'),
    },
    {
      label: t('jobDescription.steps.interviewQuestions'),
      description: t('jobDescription.steps.interviewQuestionsDescription'),
    },
    {
      label: t('jobDescription.steps.roleScorecard', 'Interview Benchmarks'),
      description: t('jobDescription.steps.roleScorecardDescription', 'The criteria every candidate is measured against'),
    },
  ];

  const jobType = [
    { value: "on_site", label: t('jobDescription.form.jobTypes.on_site', 'On-Site') },
    { value: "hybrid", label: t('jobDescription.form.jobTypes.hybrid', 'Hybrid') },
    { value: "remote", label: t('jobDescription.form.jobTypes.remote', 'Remote') },
  ];

  const resetAndClose = () => {
    setActiveStep(0);
    setRequiredSkillsInput("");
    setNiceToHaveSkillsInput("");
    setQuestionInput("");
    setQuestionType("concept");
    setEditingQuestionIndex(null);
    setEditedQuestion("");
    setEditedQuestionType("concept");
    setMcqOptions(["", "", "", ""]);
    setMcqCorrectIndex(0);
    setShowAiGeneratorModal(false);
    setShowQuestionBankModal(false);
    setShowProfilePickerModal(false);
    onClose();
  };

  const handleClose = () => {
    setShowCancelConfirm(true);
  };

  const handleNext = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, steps.length - 1));
  };

  const handleBack = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const handleSkillAdd = (skillType, value) => {
    if (!value.trim()) return;

    const currentSkills = jobDescriptionForm[skillType] || [];
    if (!currentSkills.includes(value.trim())) {
      const updatedSkills = [...currentSkills, value.trim()];
      handleInputChange({
        target: {
          name: skillType,
          value: updatedSkills,
        },
      });
    }

    if (skillType === "required_skills") {
      setRequiredSkillsInput("");
    } else {
      setNiceToHaveSkillsInput("");
    }
  };

  const handleSkillRemove = (skillType, skillToRemove) => {
    const currentSkills = jobDescriptionForm[skillType] || [];
    const updatedSkills = currentSkills.filter(
      (skill) => skill !== skillToRemove
    );
    handleInputChange({
      target: {
        name: skillType,
        value: updatedSkills,
      },
    });
  };

  // Question management functions
  const handleQuestionAdd = () => {
    if (!questionInput.trim()) return;

    const currentQuestions = jobDescriptionForm.questions || [];
    const newQuestion = {
      nameOfQuestion: questionInput.trim(),
      type: questionType,
    };

    if (questionType === "coding") {
      newQuestion.time_limit = questionTimeLimit > 0 ? questionTimeLimit : 5;
    }

    if (questionType === "mcq") {
      const trimmed = mcqOptions.map((s) => s.trim());
      const hasEmpty = trimmed.some((s) => !s);
      if (hasEmpty) return;
      if (mcqCorrectIndex < 0 || mcqCorrectIndex > 3) return;
      newQuestion.choices = trimmed.map((text, i) => ({
        text,
        is_correct: i === mcqCorrectIndex,
      }));
    }

    const updatedQuestions = [...currentQuestions, newQuestion];

    handleInputChange({
      target: {
        name: "questions",
        value: updatedQuestions,
      },
    });

    setQuestionInput("");
    setQuestionType("concept");
    setQuestionTimeLimit(5);
    setMcqOptions(["", "", "", ""]);
    setMcqCorrectIndex(0);
  };

  const handleQuestionEdit = (index, question) => {
    setEditingQuestionIndex(index);
    setEditedQuestion(question.nameOfQuestion || question);
    setEditedQuestionType(question.type || "concept");
    setEditedQuestionTimeLimit(question.time_limit || 5);
    if (question.type === "mcq" && question.choices) {
      setEditedMcqOptions(question.choices.map((c) => c.text || ""));
      const correctIdx = question.choices.findIndex((c) => c.is_correct);
      setEditedMcqCorrectIndex(correctIdx >= 0 ? correctIdx : 0);
    } else {
      setEditedMcqOptions(["", "", "", ""]);
      setEditedMcqCorrectIndex(0);
    }
  };

  const handleQuestionSave = (index) => {
    if (!editedQuestion.trim()) return;

    const currentQuestions = jobDescriptionForm.questions || [];
    const updatedQuestions = [...currentQuestions];

    // Handle both old string format and new object format
    const existingQuestion = updatedQuestions[index];
    if (
      typeof existingQuestion === "object" &&
      existingQuestion.nameOfQuestion
    ) {
      // New format - update the object
      updatedQuestions[index] = {
        ...existingQuestion,
        nameOfQuestion: editedQuestion.trim(),
        type: editedQuestionType,
        ...(editedQuestionType === "coding" && { time_limit: editedQuestionTimeLimit > 0 ? editedQuestionTimeLimit : 5 }),
        ...(editedQuestionType === "mcq" && {
          choices: editedMcqOptions.map((text, i) => ({
            text: text || "",
            is_correct: i === editedMcqCorrectIndex,
          })),
        }),
      };
    } else {
      // Old format - convert to new format
      updatedQuestions[index] = {
        nameOfQuestion: editedQuestion.trim(),
        type: editedQuestionType,
        ...(editedQuestionType === "coding" && { time_limit: editedQuestionTimeLimit > 0 ? editedQuestionTimeLimit : 5 }),
        ...(editedQuestionType === "mcq" && {
          choices: editedMcqOptions.map((text, i) => ({
            text: text || "",
            is_correct: i === editedMcqCorrectIndex,
          })),
        }),
      };
    }

    handleInputChange({
      target: {
        name: "questions",
        value: updatedQuestions,
      },
    });

    setEditingQuestionIndex(null);
    setEditedQuestion("");
    setEditedQuestionType("concept");
    setEditedQuestionTimeLimit(5);
    setEditedMcqOptions(["", "", "", ""]);
    setEditedMcqCorrectIndex(0);
  };

  const handleQuestionRemove = (indexToRemove, e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    const currentQuestions = jobDescriptionForm.questions || [];
    const updatedQuestions = currentQuestions.filter(
      (_, index) => index !== indexToRemove
    );

    handleInputChange({
      target: {
        name: "questions",
        value: updatedQuestions,
      },
    });
  };

  const cancelQuestionEdit = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingQuestionIndex(null);
    setEditedQuestion("");
    setEditedQuestionType("concept");
    setEditedQuestionTimeLimit(5);
    setEditedMcqOptions(["", "", "", ""]);
    setEditedMcqCorrectIndex(0);
  };

  const isStepValid = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return jobDescriptionForm.title && jobDescriptionForm.department;
      case 1:
        return (
          Array.isArray(jobDescriptionForm.required_skills) &&
          jobDescriptionForm.required_skills.length > 0
        );
      case 2:
        return jobDescriptionForm.description;
      case 3:
        return true; // Questions are optional
      case 4:
        return true; // Competency assessment is optional
      default:
        return false;
    }
  };

  const canProceedToNext = () => {
    return isStepValid(activeStep);
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Only allow submission on the final step (Interview Benchmarks / Step 5)
    if (activeStep < steps.length - 1) {
      handleNext(e);
      return;
    }

    // Validate all required fields
    if (
      !jobDescriptionForm.title ||
      !jobDescriptionForm.department ||
      !jobDescriptionForm.description
    ) {
      return;
    }
    if (
      !Array.isArray(jobDescriptionForm.required_skills) ||
      jobDescriptionForm.required_skills.length < 1
    ) {
      return;
    }

    onSubmit(e);
    setActiveStep(0);
  };

  const handleGenerateJD = async (e) => {
    setLoading(true);
    e.preventDefault();
    const userStr = localStorage.getItem("user");
    let companyName = "";

    const selectedDepartment = departments.find(
      (dep) => dep.id === jobDescriptionForm.department
    );
    const departmentName = selectedDepartment
      ? selectedDepartment.name
      : "Unknown";

    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        if (
          userObj.admin_companies &&
          Array.isArray(userObj.admin_companies) &&
          userObj.admin_companies.length > 0
        ) {
          companyName = userObj.admin_companies[0].split(",")[0].trim();
        }
      } catch (e) {
        console.error("Error parsing 'user' from localStorage", e);
      }
    }

    console.log("Extracted companyName:", companyName);
    const {
      questions,
      is_public,
      salary_range_min,
      salary_range_max,
      title,
      ...jobDescriptionFormWithoutQuestions
    } = jobDescriptionForm;

    const salary_range =
      salary_range_min && salary_range_max
        ? `₹${parseFloat(salary_range_min).toFixed(2)} - ₹${parseFloat(
          salary_range_max
        ).toFixed(2)}`
        : "";

    const payload = {
      ...jobDescriptionFormWithoutQuestions,
      job_title: title,
      salary_range: salary_range,
      company_name: companyName,
      department: departmentName,
      language_code: currentLanguageCode,
      // work_type: "remote",
    };

    try {
      const response = await api.post(
        "job-descriptions/generate-job-description/",
        payload,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data && response.data.generated_description) {
        let jobDescriptionText = response.data.generated_description;

        // Remove markdown header for job title (e.g., "## Intern", "### Intern", etc.)
        const titleRegex = new RegExp(
          `^#{1,3}\\s*${jobDescriptionForm.title}\\s*`,
          "gm"
        );
        jobDescriptionText = jobDescriptionText.replace(titleRegex, "");

        // Replace department ID with name (if needed)
        jobDescriptionText = jobDescriptionText.replace(
          new RegExp(`Department ${jobDescriptionForm.department}`, "g"),
          departmentName
        );

        // Convert Markdown to HTML
        const htmlContent = convertMarkdownToHtml(jobDescriptionText);

        handleInputChange({
          target: {
            name: "description",
            value: htmlContent,
          },
        });
      }
      let jobDescriptionText = response.data.generated_description;

      // Remove any header (H1–H6) at the start that contains the job title
      const titlePattern = new RegExp(
        `^\\s*#{1,6}\\s*.*${jobDescriptionForm.title}.*$\\s*`,
        "im"
      );
      jobDescriptionText = jobDescriptionText.replace(titlePattern, "");

      // Now continue with your department replacement and markdown conversion:
      jobDescriptionText = jobDescriptionText.replace(
        new RegExp(`Department ${jobDescriptionForm.department}`, "g"),
        departmentName
      );

      const htmlContent = convertMarkdownToHtml(jobDescriptionText);
      handleInputChange({
        target: {
          name: "description",
          value: htmlContent,
        },
      });
      console.log("changes");
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.error ||
          "Failed to generate job description. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <TextField
                  fullWidth
                  required
                  id="job-title"
                  name="title"
                  label={t('jobDescription.form.jobTitle')}
                  placeholder={t('jobDescription.form.jobTitlePlaceholder')}
                  value={jobDescriptionForm.title || ""}
                  onChange={handleInputChange}
                  className="bg-white"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </div>

              <FormControl fullWidth required className="bg-white" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel id="job-department-label">{t('jobDescription.form.department','Department')}</InputLabel>
                <Select
                  labelId="job-department-label"
                  id="job-department"
                  name="department"
                  value={jobDescriptionForm.department || ""}
                  onChange={handleInputChange}
                  label="Department"
                >
                  <MenuItem value="">{t('jobDescription.form.selectDepartment')}</MenuItem>
                  {departments.map((dep) => (
                    <MenuItem key={dep.id} value={dep.id}>
                      {dep.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth className="bg-white" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel id="status-label">{t('jobDescription.status','Status')}</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={jobDescriptionForm.status || "open"}
                  onChange={handleInputChange}
                  label="Status"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth className="bg-white" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel id="employment-type-label">
                  {t('jobDescription.form.employmentType','Employment Type')}
                </InputLabel>
                <Select
                  labelId="employment-type-label"
                  id="employment-type"
                  name="employment_type"
                  value={jobDescriptionForm.employment_type || ""}
                  onChange={handleInputChange}
                  label="Employment Type"
                >
                  <MenuItem value="">{t('jobDescription.form.selectType', 'Select type')}</MenuItem>
                  {employmentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth className="bg-white" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel id="experience-level-label">
                  {t('jobDescription.form.experienceLevel','Experience Level')}
                </InputLabel>
                <Select
                  labelId="experience-level-label"
                  id="experience-level"
                  name="experience_level"
                  value={jobDescriptionForm.experience_level || ""}
                  onChange={handleInputChange}
                  label="Experience Level"
                >
                  <MenuItem value="">{t('jobDescription.form.selectExperienceLevel', 'Select experience level')}</MenuItem>
                  {experienceLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth className="bg-white" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel id="interview-style-label">
                  {t('jobDescription.form.interviewStyle','Interview Style')}
                </InputLabel>
                <Select
                  labelId="interview-style-label"
                  id="interview-style"
                  name="interview_style"
                  value={jobDescriptionForm.interview_style || "default"}
                  onChange={handleInputChange}
                  label="Interview Style"
                >
                  {interviewStyles.map((style) => (
                    <MenuItem key={style.value} value={style.value}>
                      {style.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required className="bg-white" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel id="job-type-label">
                  {t('jobDescription.form.jobType','Job Type')}
                </InputLabel>
                <Select
                  labelId="job-type-label"
                  id="job-type"
                  name="work_type"
                  value={jobDescriptionForm.work_type || "on_site"}
                  onChange={handleInputChange}
                  label="Job Type"
                >
                  {jobType.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {jobDescriptionForm.interview_style === "campus_recruitment" && (
                <TextField
                  fullWidth
                  name="campus_questions_per_category"
                  label={t('jobDescription.form.questionsPerCategory','Questions per Category')}
                  type="number"
                  value={jobDescriptionForm.campus_questions_per_category || 3}
                  onChange={handleInputChange}
                  className="bg-white"
                />
              )}

              <TextField
                fullWidth
                id="job-title"
                name="location"
                label={t('jobDescription.form.location','Location')}
                placeholder={t('jobDescription.form.locationPlaceholder','e.g. 123 Main Street, New York, NY 10001')}
                value={jobDescriptionForm.location || ""}
                onChange={handleInputChange}
                className="bg-white"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <div className="md:col-span-2 flex justify-end">
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => setShowAddDepartmentModal(true)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      borderColor: "#3b82f6",
                      color: "#3b82f6",
                      px: 3,
                      "&:hover": {
                        backgroundColor: "#eff6ff",
                        borderColor: "#2563eb",
                      },
                    }}
                  >
                    {t('jobDescription.buttons.addDepartment', '+ ADD DEPARTMENT')}
                  </Button>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            {/* Required Skills */}
            <div className="space-y-3">
              <Typography variant="subtitle1" className="text-gray-700">
                {t('jobDescription.form.requiredSkills')}
                <span className="text-red-600 ml-0.5" aria-hidden>
                  *
                </span>
              </Typography>
              <Typography variant="caption" className="block text-gray-600">
                {t(
                  'jobDescription.form.requiredSkillsHelper',
                  'Add at least one skill. You cannot continue without a required skill.'
                )}
              </Typography>
              <TextField
                fullWidth
                required
                label={t('jobDescription.form.addRequiredSkill')}
                placeholder={t('jobDescription.form.addSkillPlaceholder')}
                value={requiredSkillsInput}
                onChange={(e) => setRequiredSkillsInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSkillAdd("required_skills", requiredSkillsInput);
                  }
                }}
                className="bg-white"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() =>
                          handleSkillAdd("required_skills", requiredSkillsInput)
                        }
                        disabled={!requiredSkillsInput.trim()}
                        className="text-blue-600"
                      >
                        {t('common.add', 'Add')}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <div className="flex flex-wrap gap-2">
                {(jobDescriptionForm.required_skills || []).map(
                  (skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      onDelete={() =>
                        handleSkillRemove("required_skills", skill)
                      }
                      className="bg-blue-100 text-blue-800 border border-blue-200"
                      sx={{
                        backgroundColor: "#dbeafe",
                        color: "#1e40af",
                        "& .MuiChip-deleteIcon": { color: "#1e40af" },
                      }}
                    />
                  )
                )}
              </div>
            </div>

            {/* Nice to Have Skills */}
            <div className="space-y-3">
              <Typography variant="subtitle1" className=" text-gray-700">
                {t('jobDescription.form.niceToHaveSkills')}
              </Typography>
              <TextField
                fullWidth
                label={t('jobDescription.form.addNiceToHaveSkill')}
                placeholder={t('jobDescription.form.addSkillPlaceholder')}
                value={niceToHaveSkillsInput}
                onChange={(e) => setNiceToHaveSkillsInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSkillAdd(
                      "nice_to_have_skills",
                      niceToHaveSkillsInput
                    );
                  }
                }}
                className="bg-white"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() =>
                          handleSkillAdd(
                            "nice_to_have_skills",
                            niceToHaveSkillsInput
                          )
                        }
                        disabled={!niceToHaveSkillsInput.trim()}
                        className="text-purple-600"
                      >
                        {t('common.add', 'Add')}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <div className="flex flex-wrap gap-2">
                {(jobDescriptionForm.nice_to_have_skills || []).map(
                  (skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      onDelete={() =>
                        handleSkillRemove("nice_to_have_skills", skill)
                      }
                      className="bg-purple-100 text-purple-800 border border-purple-200"
                      sx={{
                        backgroundColor: "#f3e8ff",
                        color: "#7c3aed",
                        "& .MuiChip-deleteIcon": { color: "#7c3aed" },
                      }}
                    />
                  )
                )}
              </div>
            </div>

            {/* Salary Range */}
            <div className="space-y-3">
              <Typography variant="subtitle1" className=" text-gray-700">
                {t('jobDescription.form.salaryRangeOptional')}
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  fullWidth
                  type="number"
                  label={t('jobDescription.form.minimumSalary')}
                  name="salary_range_min"
                  value={jobDescriptionForm.salary_range_min || ""}
                  onChange={handleInputChange}
                  className="bg-white"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                  }}
                  placeholder="e.g. 500000"
                />
                <TextField
                  fullWidth
                  type="number"
                  label={t('jobDescription.form.maximumSalary')}
                  name="salary_range_max"
                  value={jobDescriptionForm.salary_range_max || ""}
                  onChange={handleInputChange}
                  className="bg-white"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                  }}
                  placeholder="e.g. 800000"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <FormControlLabel
                control={
                  <Switch
                    checked={jobDescriptionForm.is_public}
                    onChange={(e) =>
                      handleInputChange({
                        target: { name: "is_public", value: e.target.checked },
                      })
                    }
                    name="is_public"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" component="span" className="">
                      {t('jobDescription.form.publicJobDescription', 'Public Job Description')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="div"
                    >
                      {t('jobDescription.form.publicJobDescriptionDescription', 'Make this job description visible to public applicants')}
                    </Typography>
                  </Box>
                }
              />
            </div>
            {/* Pooja Addded */}

            <div className="space-y-2">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <Typography variant="subtitle1" className="text-gray-700">
                    {t('jobDescription.form.descriptionLabel')} <span className="text-red-500">*</span>
                  </Typography>
                  <Button
                    startIcon={
                      loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <AutoAwesomeOutlinedIcon sx={{ color: '#3b82f6' }} />
                      )
                    }
                    variant="outlined"
                    onClick={handleGenerateJD}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      backgroundColor: "#fff",
                      color: "#3b82f6",
                      border: "1px solid #3b82f6",
                      borderRadius: "8px",
                      padding: "6px 16px",
                      "&:hover": {
                        backgroundColor: "#eff6ff",
                        border: "1px solid #2563eb",
                      },
                    }}
                  >
                    {loading ? t('jobDescription.buttons.generating') : t('jobDescription.buttons.generateJd', 'Generate JD')}
                  </Button>
              </div>

              {/* --------------------- */}
              <Box 
                className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
                sx={{
                  "& .ck-editor__editable_inline": {
                    minHeight: "250px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    padding: "20px !important",
                    border: "none !important",
                    lineHeight: 1.7,
                  },
                  "& .ck-editor__top .ck-sticky-panel .ck-toolbar": {
                    borderRadius: "8px 8px 0 0",
                    border: "none",
                    borderBottom: "1px solid #e5e7eb",
                    backgroundColor: "#f9fafb",
                  },
                  "& .ck.ck-editor__main>.ck-editor__editable:not(.ck-focused)": {
                    borderColor: "transparent"
                  },
                  "& .ck.ck-editor__main>.ck-editor__editable.ck-focused": {
                    borderColor: "transparent",
                    boxShadow: "none"
                  },
                  "& .ck-content h1, & .ck-content h2, & .ck-content h3, & .ck-content h4, & .ck-content h5, & .ck-content h6": {
                    marginTop: "1rem",
                    marginBottom: "0.75rem",
                  },
                  "& .ck-content p": {
                    marginTop: 0,
                    marginBottom: "1rem",
                  },
                  "& .ck-content ul, & .ck-content ol": {
                    marginTop: 0,
                    marginBottom: "1rem",
                  },
                  "& .ck-content li": {
                    marginBottom: "0.5rem",
                  }
                }}
              >
                  <CKEditor
                    editor={ClassicEditor}
                    data={jobDescriptionForm.description || ""}
                    onChange={(event, editor) => {
                      let data = editor.getData();
                      data = cleanEmptyListItems(data);
                      handleInputChange({
                        target: { name: "description", value: data },
                      });
                    }}
                    config={{
                      toolbar: [
                        "heading",
                        "|",
                        "bold",
                        "italic",
                        "underline",
                        "|",
                        "bulletedList",
                        "numberedList",
                        "|",
                        "outdent",
                        "indent",
                        "|",
                        "blockQuote",
                        "link",
                        "|",
                        "undo",
                        "redo",
                      ],
                      placeholder: t('jobDescription.form.descriptionPlaceholder'),
                    }}
                  />
              </Box>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {/* Header row: Title on left, Action Buttons on right with uniform height & styling */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-gray-100">
              <div>
                <Typography variant="h6" className="font-bold text-gray-800" sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                  {t('jobDescription.interviewQuestions', 'Interview Questions')}
                </Typography>
                <p className="text-xs text-gray-500 mt-0.5">
                  Standardize candidate interviews with question profiles, question bank, or AI.
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() => setShowProfilePickerModal(true)}
                  startIcon={<Layers size={15} className="text-indigo-600" />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    borderRadius: '10px',
                    borderColor: '#818cf8',
                    color: '#4f46e5',
                    height: '38px',
                    whiteSpace: 'nowrap',
                    px: 2,
                    '&:hover': {
                      borderColor: '#6366f1',
                      backgroundColor: '#eef2ff',
                    },
                  }}
                >
                  {t('interviewProfile.attachProfile', 'Attach Profile')}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() => setShowQuestionBankModal(true)}
                  startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    borderRadius: '10px',
                    borderColor: '#cbd5e1',
                    color: '#334155',
                    height: '38px',
                    whiteSpace: 'nowrap',
                    px: 2,
                    '&:hover': {
                      borderColor: '#94a3b8',
                      backgroundColor: '#f8fafc',
                    },
                  }}
                >
                  {t('questionBank.addFromBank', 'Add from Question Bank')}
                </Button>

                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  onClick={() => setShowAiGeneratorModal(true)}
                  startIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #0284c7 0%, #c026d3 100%)",
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    borderRadius: '10px',
                    height: '38px',
                    whiteSpace: 'nowrap',
                    px: 2,
                    boxShadow: '0 2px 8px rgba(14, 165, 233, 0.25)',
                  }}
                >
                  {t('jobDescription.generateWithAI', 'Generate with AI')}
                </Button>

                <div className="h-[38px] px-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 font-bold text-xs flex items-center justify-center whitespace-nowrap shadow-2xs">
                  {jobDescriptionForm.questions?.length || 0} {t('jobDescription.tableQuestions', 'questions')}
                </div>

                {attachedProfile && (
                  <div
                    className="h-[38px] px-3 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold text-xs flex items-center gap-2 shadow-2xs whitespace-nowrap"
                    title={`Attached Profile: ${attachedProfile.name}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                    <span className="font-bold max-w-[140px] truncate">
                      {attachedProfile.name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Add Question Input */}
            <div className="space-y-3">
              <Typography variant="subtitle1" className=" text-gray-700">
                {t('jobDescription.addQuestionTitle', 'Add Interview Question')}
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-3">
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('jobDescription.question', 'Enter interview question')}
                    placeholder={t('jobDescription.questionPlaceholder')}
                    value={questionInput}
                    onChange={(e) => setQuestionInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && e.ctrlKey) {
                        e.preventDefault();
                        handleQuestionAdd();
                      }
                    }}
                    className="bg-white"
                  />
                </div>
                <div className="md:col-span-1">
                  <FormControl fullWidth className="bg-white">
                    <InputLabel id="question-type-label">
                      {t('jobDescription.questionType','Question Type')}
                    </InputLabel>
                    <Select
                      labelId="question-type-label"
                      id="question-type"
                      value={questionType}
                      onChange={(e) => setQuestionType(e.target.value)}
                      label="Question Type"
                    >
                      {questionTypeOptions.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              {questionType === "coding" && (
                <TextField
                  label={t('jobDescription.timeLimit', 'Time Limit (minutes)')}
                  type="number"
                  size="small"
                  value={questionTimeLimit}
                  onChange={(e) => setQuestionTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                  inputProps={{ min: 1, max: 60 }}
                  className="bg-white"
                  sx={{ width: 200 }}
                  helperText={t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}
                />
              )}
              {questionType === "mcq" && (
                <div className="space-y-2">
                  <Typography variant="subtitle2" className=" text-gray-700">
                    {t('jobDescription.multipleChoiceOptions')}
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {mcqOptions.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="mcq-correct"
                          checked={mcqCorrectIndex === i}
                          onChange={() => setMcqCorrectIndex(i)}
                          aria-label={`Mark answer ${String.fromCharCode(65 + i)} correct`}
                        />
                        <TextField
                          fullWidth
                          label={`${t('jobDescription.answer', 'Answer')} ${String.fromCharCode(65 + i)}`}
                          value={opt}
                          onChange={(e) =>
                            setMcqOptions((prev) =>
                              prev.map((v, idx) => (idx === i ? e.target.value : v))
                            )
                          }
                          className="bg-white"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleQuestionAdd}
                  disabled={
                    !questionInput.trim() ||
                    (questionType === "mcq" &&
                      (mcqOptions.some((o) => !o.trim()) ||
                        mcqCorrectIndex < 0 ||
                        mcqCorrectIndex > 3))
                  }
                  className="text-green-600 border-green-600 hover:bg-green-50"
                  variant="outlined"
                  startIcon={<Plus size={14} />}
                >
                  {t('jobDescription.addQuestion')}
                </Button>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="  text-gray-600">{t('jobDescription.interviewQuestions', 'Interview Questions')}</span>
                <span className="bg-blue-100 text-blue-800  px-2 py-1 rounded-full ">
                  {jobDescriptionForm.questions?.length || 0}
                </span>
              </div>

              {durationInfo.total_questions > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-blue-900 flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-blue-600" />
                      Auto-Calculated Interview Duration
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="bg-white border border-blue-200 text-blue-800 px-2 py-0.5 rounded font-semibold text-[11px]">
                        Min: {durationInfo.minimum_duration_minutes} min
                      </span>
                      <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold text-[11px]">
                        Recommended: {durationInfo.recommended_duration_minutes} min
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-[11px] leading-relaxed">
                    {formatDurationBreakdownText(durationInfo.breakdown, durationInfo.total_questions, durationInfo.minimum_duration_minutes)}
                  </p>
                </div>
              )}

              {jobDescriptionForm.questions &&
                jobDescriptionForm.questions.length > 0 ? (
                <div className="space-y-3">
                  {jobDescriptionForm.questions.map((question, index) => {
                    // Handle both old string format and new object format
                    const questionText =
                      typeof question === "object" && question.nameOfQuestion
                        ? question.nameOfQuestion
                        : question;
                    const questionType =
                      typeof question === "object" && question.type
                        ? question.type
                        : "general";

                    const isProfileQuestion = Boolean(
                      typeof question === "object" && (
                        question.profileId ||
                        question.profile_id ||
                        question.profileQuestionId ||
                        question.profile_question_id ||
                        question.profileName ||
                        question.profile_name
                      )
                    );

                    const questionProfileName =
                      (typeof question === "object" && (question.profileName || question.profile_name)) ||
                      (isProfileQuestion && attachedProfile?.name) ||
                      (isProfileQuestion && selectedJobDescription?.attached_profiles?.[0]?.name) ||
                      (isProfileQuestion ? "Interview Profile" : null);

                    return (
                      <div
                        key={index}
                        className={`bg-gray-50 p-3 rounded-lg border-l-4 ${questionProfileName ? 'border-l-emerald-500' : 'border-l-blue-400'} hover:shadow-xs transition-shadow`}
                      >
                        {questionProfileName && (
                          <div className="mb-2 pb-1.5 border-b border-gray-200/60 flex items-center justify-between">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 text-[11px] font-semibold shadow-2xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                              <Layers size={11} className="text-emerald-700 stroke-[2.4] shrink-0" />
                              <span className="font-bold truncate max-w-[260px]">{questionProfileName}</span>
                            </span>
                          </div>
                        )}

                        <div className="flex items-start gap-3">
                          <div className="flex flex-col gap-1 shrink-0">
                            <span className="bg-blue-100 text-blue-800  px-2 py-1 rounded-full ">
                              Q{index + 1}
                            </span>
                            <div className="flex items-center gap-1">
                              <span
                                className={` px-2 py-1 rounded-full  ${questionType === "concept"
                                  ? "bg-purple-100 text-purple-800"
                                  : questionType === "coding"
                                    ? "bg-orange-100 text-orange-800"
                                    : questionType === "mcq"
                                      ? "bg-green-100 text-green-800"
                                      : questionType === "behavioral"
                                        ? "bg-pink-100 text-pink-800"
                                        : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {questionTypeOptions.find(
                                  (opt) => opt.value === questionType
                                )?.label || questionType}
                              </span>
                              {questionType === "coding" && (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-[10px] font-semibold text-center whitespace-nowrap">
                                  {typeof question === "object" ? question.time_limit || 5 : 5} min
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            {editingQuestionIndex === index ? (
                              <div className="space-y-3">
                                <textarea
                                  value={editedQuestion}
                                  onChange={(e) =>
                                    setEditedQuestion(e.target.value)
                                  }
                                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                                  rows="2"
                                />
                                <FormControl fullWidth size="small">
                                  <InputLabel id="edit-question-type-label">
                                    Question Type
                                  </InputLabel>
                                  <Select
                                    labelId="edit-question-type-label"
                                    value={editedQuestionType}
                                    onChange={(e) =>
                                      setEditedQuestionType(e.target.value)
                                    }
                                    label="Question Type"
                                  >
                                    {questionTypeOptions.map((type) => (
                                      <MenuItem
                                        key={type.value}
                                        value={type.value}
                                      >
                                        {type.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {editedQuestionType === "coding" && (
                                  <TextField
                                    label={t('jobDescription.timeLimit', 'Time Limit (minutes)')}
                                    type="number"
                                    size="small"
                                    value={editedQuestionTimeLimit}
                                    onChange={(e) => setEditedQuestionTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                                    inputProps={{ min: 1, max: 60 }}
                                    className="bg-white"
                                    helperText={t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}
                                  />
                                )}
                                {editedQuestionType === "mcq" && (
                                  <div className="space-y-2">
                                    <Typography variant="subtitle2" className=" text-gray-700">
                                      {t('jobDescription.multipleChoiceOptions', 'Multiple Choice Options')}
                                    </Typography>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {editedMcqOptions.map((opt, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                          <input
                                            type="radio"
                                            name="edit-mcq-correct"
                                            checked={editedMcqCorrectIndex === i}
                                            onChange={() => setEditedMcqCorrectIndex(i)}
                                            aria-label={`Mark answer ${String.fromCharCode(65 + i)} correct`}
                                          />
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label={`${t('jobDescription.answer', 'Answer')} ${String.fromCharCode(65 + i)}`}
                                            value={opt}
                                            onChange={(e) =>
                                              setEditedMcqOptions((prev) =>
                                                prev.map((v, idx) => (idx === i ? e.target.value : v))
                                              )
                                            }
                                            className="bg-white"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleQuestionSave(index);
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                    disabled={
                                      !editedQuestion.trim() ||
                                      (editedQuestionType === "mcq" &&
                                        editedMcqOptions.some((o) => !o.trim()))
                                    }
                                  >
                                    <Save size={12} />
                                    {t('common.save', 'Save')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => cancelQuestionEdit(e)}
                                    className="flex items-center gap-1 px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                  >
                                    <X size={12} />
                                    {t('common.cancel', 'Cancel')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between">
                                <span className=" text-gray-700 flex-1 mr-2">
                                  {questionText}
                                </span>
                                <div className="flex gap-1 shrink-0">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleQuestionEdit(index, question);
                                    }}
                                    className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                    title="Edit question"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => handleQuestionRemove(index, e)}
                                    className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                    title="Delete question"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                            {questionType === "mcq" && Array.isArray(question.choices) && question.choices.length > 0 && (
                              <div className="mt-2 ml-2 space-y-1">
                                {question.choices.map((choice, cIdx) => (
                                  <div key={cIdx} className="flex items-center gap-2 text-sm">
                                    <span className=" text-gray-600 w-5">
                                      {String.fromCharCode(65 + cIdx)}.
                                    </span>
                                    <span className=" text-gray-700 flex-1">{choice.text}</span>
                                    {choice.is_correct && (
                                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">
                                        {t('jobDescription.modal.correct', 'Correct')}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className=" text-gray-500 mb-3">
                    {t('jobDescription.noQuestionsAdded')}
                  </div>
                  <p className=" text-gray-400">
                    {t('jobDescription.addFirstQuestion', 'Add your first question')}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <BenchmarksEditor
            compact
            mode="local"
            rows={jobDescriptionForm.benchmarks || []}
            onChange={(next) =>
              handleInputChange({ target: { name: "benchmarks", value: next } })
            }
            approveOnSave={Boolean(jobDescriptionForm.benchmarks_approve)}
            onApproveOnSaveChange={(checked) =>
              handleInputChange({
                target: { name: "benchmarks_approve", value: checked },
              })
            }
            jdFields={{
              title: jobDescriptionForm.title,
              description: jobDescriptionForm.description,
              required_skills: jobDescriptionForm.required_skills,
              nice_to_have_skills: jobDescriptionForm.nice_to_have_skills,
              experience_level: jobDescriptionForm.experience_level,
              interview_style: jobDescriptionForm.interview_style,
            }}
            interviewStyle={jobDescriptionForm.interview_style}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={undefined}
        aria-labelledby="job-description-title"
        className="flex items-center justify-center p-4"
      >
        <Box
          className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full relative flex flex-col max-h-[90vh] overflow-hidden"
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div className="relative px-6 py-3 text-white overflow-hidden shrink-0" style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)" }}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <div className="relative z-10 pr-12">
              <Typography
                variant="h6"
                className="font-semibold mb-0.5 text-white"
                sx={{ color: "white !important", fontSize: "1.125rem", lineHeight: 1.2 }}
              >
                {selectedJobDescription
                  ? t('jobDescription.modal.editTitle', 'Edit Job Description')
                  : t('jobDescription.modal.createTitle', 'Create Job Description')}
              </Typography>
              <Typography
                variant="body2"
                className="opacity-90 text-white font-medium"
                sx={{ color: "rgba(255, 255, 255, 0.95) !important", fontSize: "0.8125rem", lineHeight: 1.2 }}
              >
                {selectedJobDescription
                  ? t('jobDescription.modal.editSubtitle', 'Update the job description details and requirements')
                  : t('jobDescription.modal.createSubtitle', 'Follow the steps to create a comprehensive job posting')}
              </Typography>
            </div>

            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 10,
                right: 14,
                color: "white",
                zIndex: 10,
                padding: "6px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {/* Horizontal Stepper */}
          <div className="bg-white border-b border-gray-100 px-6 pt-3.5 pb-2 shrink-0">
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((step, index) => {
                const canNavigate = index <= activeStep || isStepValid(activeStep);
                return (
                  <Step key={step.label}>
                    <Tooltip title={step.description} arrow placement="bottom" enterDelay={150}>
                      <StepLabel
                        onClick={(e) => {
                          if (e && e.preventDefault) e.preventDefault();
                          if (canNavigate) {
                            setActiveStep(index);
                          }
                        }}
                        sx={{ cursor: canNavigate ? 'pointer' : 'default' }}
                      >
                        <div className="flex flex-col items-center">
                          <Typography
                            variant="subtitle2"
                            className="font-bold text-gray-800 leading-tight"
                            sx={{ fontSize: '0.8125rem' }}
                          >
                            {step.label}
                          </Typography>
                        </div>
                      </StepLabel>
                    </Tooltip>
                  </Step>
                );
              })}
            </Stepper>
          </div>

          {/* Content */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              <div className="px-6 py-4">
              {/* Step Content */}
              {activeStep < steps.length ? (
                <div className="space-y-6">

                  {/* Step Content */}
                  <div className={activeStep === 4 ? "max-w-5xl mx-auto" : "max-w-4xl mx-auto"}>
                    {getStepContent(activeStep)}
                  </div>
                </div>
              ) : (
                /* Show completion message when all steps are done */
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="h6" className="text-green-600 mb-2" sx={{ fontSize: '1.125rem' }}>
                    {t('jobDescription.completion.title', 'All steps completed!')}
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 mb-4" sx={{ fontSize: '0.875rem' }}>
                    {t('jobDescription.completion.subtitle', 'Your job description is ready to be {{action}}.', {
                      action: selectedJobDescription ? t('common.updated', 'updated') : t('common.created', 'created')
                    })}
                  </Typography>
                  <div className="flex justify-center gap-3">
                    <Button
                      onClick={handleBack}
                      variant="outlined"
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        borderWidth: 2,
                        borderColor: '#d1d5db',
                        color: '#374151',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb',
                        },
                      }}
                    >
                      {t('jobDescription.completion.backToReview', 'Back to Review')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        px: 6,
                        py: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 3,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      {selectedJobDescription
                        ? t('jobDescription.completion.updateJobDescription', 'Update Job Description')
                        : t('jobDescription.completion.createJobDescription', 'Create Job Description')}
                    </Button>
                  </div>
                </Box>
              )}

              {jobDescriptionError && (
                <Alert severity="error" className="mt-4">
                  {jobDescriptionError}
                </Alert>
              )}
              </div>
            </div>

            {activeStep < steps.length && (
              <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-2.5">
                <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
                  <Button
                    type="button"
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{
                      minWidth: "84px",
                      height: "38px",
                      px: 3.5,
                      py: 0,
                      borderRadius: "999px",
                      border: "1.5px solid #e5e7eb",
                      color: "#4b5563",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "&:hover": {
                        border: "1.5px solid #d1d5db",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    {t('common.back', 'Back')}
                  </Button>

                  <div className="flex gap-2.5">
                    <Button
                      type="button"
                      onClick={handleClose}
                      variant="outlined"
                      sx={{
                        minWidth: "92px",
                        height: "38px",
                        px: 3.5,
                        py: 0,
                        borderRadius: "999px",
                        border: "1.5px solid #e5e7eb",
                        color: "#4b5563",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        "&:hover": {
                          border: "1.5px solid #d1d5db",
                          backgroundColor: "#f9fafb",
                        },
                      }}
                    >
                      {t("common.cancel", "Cancel")}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={(e) => {
                        if (e && e.preventDefault) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                        if (activeStep === steps.length - 1) {
                          handleSubmit(e);
                        } else {
                          handleNext(e);
                        }
                      }}
                      disabled={!canProceedToNext()}
                      sx={{
                        minWidth: "120px",
                        height: "38px",
                        px: 4,
                        py: 0,
                        borderRadius: "999px",
                        background:
                          "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)",
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #0284c7 0%, #c120d5 100%)",
                          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
                        },
                      }}
                    >
                      {activeStep === steps.length - 1
                        ? selectedJobDescription
                          ? t("common.update", "Update")
                          : t("common.create", "Create")
                        : t("common.continue", "Continue")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        aria-labelledby="cancel-job-description-title"
        aria-describedby="cancel-job-description-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: "90%", sm: 460 },
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle id="cancel-job-description-title" sx={{ fontWeight: 700, pb: 1 }}>
          {t("jobDescription.modal.cancelConfirmationTitle", "Discard changes?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="cancel-job-description-description"
            sx={{ color: "text.primary", opacity: 0.9 }}
          >
            {t(
              "jobDescription.modal.cancelConfirmation",
              "Are you sure you want to cancel? Unsaved changes will be lost."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setShowCancelConfirm(false)}
            sx={{
              borderRadius: "999px",
              px: 2.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowCancelConfirm(false);
              resetAndClose();
            }}
            sx={{
              borderRadius: "999px",
              px: 3,
              textTransform: "none",
              fontWeight: 700,
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
              },
            }}
          >
            {t("jobDescription.modal.confirmCancelCta", "Yes, discard")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Question Generator Modal */}
      <AIQuestionGeneratorModal
        open={showAiGeneratorModal}
        onClose={() => setShowAiGeneratorModal(false)}
        onAddQuestions={handleAiQuestionsAdded}
        jobTitle={jobDescriptionForm.title}
        experienceLevel={jobDescriptionForm.experience_level}
        requiredSkills={jobDescriptionForm.required_skills}
        niceToHaveSkills={jobDescriptionForm.nice_to_have_skills}
        existingQuestions={jobDescriptionForm.questions}
        companyId={departments.find((dep) => dep.id === jobDescriptionForm.department || String(dep.id) === String(jobDescriptionForm.department))?.company}
      />
      <QuestionBankPickerModal
        open={showQuestionBankModal}
        onClose={() => setShowQuestionBankModal(false)}
        onAddQuestions={handleAiQuestionsAdded}
        existingQuestions={jobDescriptionForm.questions}
      />
      <InterviewProfilePickerModal
        open={showProfilePickerModal}
        onClose={() => setShowProfilePickerModal(false)}
        onAttach={handleProfileAttached}
        onDetach={handleDetachProfile}
        attachedProfileId={attachedProfile?.id}
        existingQuestions={jobDescriptionForm.questions || []}
        jobDescriptionId={selectedJobDescription?.id || jobDescriptionForm?.id}
      />
    </>
  );
}
